/**
 * Session-only legend visibility for plot curves.
 *
 * Legend masking is kept separate from persisted `curvePrefs.visible` so users
 * can hide traces via the Plotly legend without mutating widget config.
 *
 * @packageDocumentation
 * @module CurveSessionVisibility
 * @public
 */

import { CurveId, PlotPreferences } from '../config/plotTypes';

// -- type definitions --

/**
 * Session legend visibility overrides keyed by curve id.
 *
 * Only hidden curves are stored (`false`). Absent keys mean visible, subject
 * to persisted preferences.
 */
export type CurveSessionVisibility = Partial<Record<CurveId, false>>;

/** Minimal trace shape for legend visibility helpers. */
export interface CurveSessionVisibilityTraceLike {
  readonly meta?: unknown;
  readonly name?: string;
  readonly legendgroup?: string;
  readonly visible?: boolean | 'legendonly';
}

/** Optional Plotly legend event trace payload used as a resolution fallback. */
export interface LegendToggleEventTraceLike
  extends CurveSessionVisibilityTraceLike {
  readonly uid?: string;
}

// -- helper functions --

/**
 * Reads a curve id from Plotly trace metadata.
 *
 * @param trace - Plotly trace with optional `meta.curveId`.
 * @returns The curve id when present, otherwise `undefined`.
 */
export function getCurveIdFromTrace(
  trace: CurveSessionVisibilityTraceLike | undefined
): CurveId | undefined {
  if (!trace || typeof trace.meta !== 'object' || trace.meta === null) {
    return undefined;
  }

  const curveId = (trace.meta as { curveId?: string }).curveId;

  return typeof curveId === 'string' && curveId.length > 0
    ? curveId
    : undefined;
}

/**
 * Returns whether a trace is currently rendered as visible.
 *
 * @param trace - Plotly trace to inspect.
 * @returns `true` when the trace participates in hover/render.
 */
export function isTracePlotlyVisible(
  trace: CurveSessionVisibilityTraceLike | undefined
): boolean {
  return trace?.visible !== false && trace?.visible !== 'legendonly';
}

/**
 * Resolves whether a curve is visible for the current session.
 *
 * Persisted `curvePrefs.visible === false` always wins. Session overrides
 * only apply to curves that would otherwise be rendered.
 *
 * @param curveId - Target curve id.
 * @param prefs - Plot preferences containing persisted visibility.
 * @param sessionVisibility - Session legend overrides.
 * @returns `true` when the curve should be shown.
 */
export function isCurveSessionVisible(
  curveId: CurveId,
  prefs: PlotPreferences,
  sessionVisibility: CurveSessionVisibility | undefined
): boolean {
  if (prefs.curvePrefs[curveId]?.visible === false) {
    return false;
  }

  return sessionVisibility?.[curveId] !== false;
}

/**
 * Returns whether any curves are session-hidden via the legend.
 *
 * @param sessionVisibility - Session legend overrides.
 * @returns `true` when at least one curve is legend-masked.
 */
export function hasSessionHiddenCurves(
  sessionVisibility: CurveSessionVisibility | undefined
): boolean {
  if (!sessionVisibility) {
    return false;
  }

  return Object.values(sessionVisibility).some((value) => value === false);
}

/**
 * Returns whether any sensor trace is currently rendered as legend-only.
 *
 * @param plotData - Plotly trace array passed to the plot.
 * @returns `true` when at least one sensor trace uses `visible: 'legendonly'`.
 */
export function hasLegendOnlySensorTraces(
  plotData: readonly CurveSessionVisibilityTraceLike[]
): boolean {
  return plotData.some(
    (trace) =>
      getCurveIdFromTrace(trace) !== undefined &&
      trace.visible === CURVE_SESSION_LEGEND_MASK_VISIBLE
  );
}

/**
 * Returns whether legend double-click should restore all session-hidden sensors.
 *
 * @param sessionVisibility - Session legend overrides.
 * @param plotData - Plotly trace array passed to the plot.
 * @returns `true` when session or rendered data still has masked sensor traces.
 */
export function shouldResetCurveSessionLegendVisibility(
  sessionVisibility: CurveSessionVisibility | undefined,
  plotData: readonly CurveSessionVisibilityTraceLike[]
): boolean {
  return (
    hasSessionHiddenCurves(sessionVisibility) ||
    hasLegendOnlySensorTraces(plotData)
  );
}

/**
 * Resolves curve ids affected by a legend click.
 *
 * When the clicked trace belongs to a legend group, every trace in that group
 * is toggled together to mirror Plotly's grouped legend behavior.
 *
 * @param plotData - Current Plotly trace array.
 * @param curveNumber - Index of the clicked legend entry.
 * @param eventTrace - Trace payload from the Plotly legend event, when available.
 * @returns Curve ids to toggle; empty when the trace is not session-managed.
 */
export function resolveLegendToggleCurveIds(
  plotData: readonly CurveSessionVisibilityTraceLike[],
  curveNumber: number,
  eventTrace?: LegendToggleEventTraceLike
): readonly CurveId[] {
  const clickedTrace =
    plotData[curveNumber] ??
    findTraceByLegendEvent(plotData, eventTrace);

  if (!clickedTrace) {
    return [];
  }

  const clickedCurveId =
    getCurveIdFromTrace(clickedTrace) ?? getCurveIdFromTrace(eventTrace);

  if (!clickedCurveId) {
    return [];
  }

  const legendgroup = clickedTrace.legendgroup ?? eventTrace?.legendgroup;

  if (!legendgroup) {
    return [clickedCurveId];
  }

  const curveIds = new Set<CurveId>();

  plotData.forEach((trace) => {
    if (trace.legendgroup !== legendgroup) {
      return;
    }

    const curveId = getCurveIdFromTrace(trace);

    if (curveId) {
      curveIds.add(curveId);
    }
  });

  return curveIds.size > 0 ? [...curveIds] : [clickedCurveId];
}

/**
 * Finds a plot trace matching the Plotly legend event payload.
 *
 * @param plotData - Current Plotly trace array.
 * @param eventTrace - Trace payload from the Plotly legend event.
 * @returns Matching plot trace, if any.
 */
export function findTraceByLegendEvent(
  plotData: readonly CurveSessionVisibilityTraceLike[],
  eventTrace: LegendToggleEventTraceLike | undefined
): CurveSessionVisibilityTraceLike | undefined {
  if (!eventTrace) {
    return undefined;
  }

  const curveIdFromEvent = getCurveIdFromTrace(eventTrace);

  if (curveIdFromEvent) {
    const byCurveId = plotData.find(
      (trace) => getCurveIdFromTrace(trace) === curveIdFromEvent
    );

    if (byCurveId) {
      return byCurveId;
    }
  }

  if (eventTrace.name) {
    return plotData.find((trace) => trace.name === eventTrace.name);
  }

  return undefined;
}

/**
 * Toggles session legend visibility for one or more curves.
 *
 * When any target curve is currently visible, all targets are hidden.
 * Otherwise all targets are shown again by removing their session overrides.
 *
 * @param previous - Previous session visibility map.
 * @param curveIds - Curve ids affected by the legend click.
 * @returns Updated session visibility map.
 */
export function toggleCurveSessionVisibility(
  previous: CurveSessionVisibility,
  curveIds: readonly CurveId[]
): CurveSessionVisibility {
  if (curveIds.length === 0) {
    return previous;
  }

  const anyVisible = curveIds.some((curveId) => previous[curveId] !== false);

  if (anyVisible) {
    return {
      ...previous,
      ...Object.fromEntries(curveIds.map((curveId) => [curveId, false])),
    } as CurveSessionVisibility;
  }

  const next = { ...previous };

  curveIds.forEach((curveId) => {
    delete next[curveId];
  });

  return next;
}

/**
 * Drops session overrides for curves no longer present in preferences.
 *
 * @param previous - Previous session visibility map.
 * @param prefs - Current plot preferences.
 * @returns Pruned session visibility map.
 */
export function pruneCurveSessionVisibility(
  previous: CurveSessionVisibility,
  prefs: PlotPreferences
): CurveSessionVisibility {
  const next: CurveSessionVisibility = {};

  Object.keys(previous).forEach((curveId) => {
    if (prefs.curvePrefs[curveId] !== undefined) {
      next[curveId] = false;
    }
  });

  return next;
}

/**
 * Clears all session legend visibility overrides.
 *
 * @returns An empty session visibility map.
 */
export function clearCurveSessionVisibility(): CurveSessionVisibility {
  return {};
}

/** Plotly visibility for session legend-masked sensor traces. */
export const CURVE_SESSION_LEGEND_MASK_VISIBLE = 'legendonly' as const;

/**
 * Applies session legend visibility to Plotly traces.
 *
 * Hidden curves remain in the trace array with `visible: 'legendonly'` so the
 * legend entry stays clickable for restore.
 *
 * @param traces - Plotly traces after relation enrichment.
 * @param sessionVisibility - Session legend overrides.
 * @returns Traces with legend-only visibility applied to session-hidden curves.
 */
export function applyCurveSessionVisibilityToTraces<
  T extends CurveSessionVisibilityTraceLike,
>(traces: readonly T[], sessionVisibility: CurveSessionVisibility | undefined): T[] {
  if (sessionVisibility === undefined) {
    return [...traces];
  }

  return traces.map((trace) => {
    const curveId = getCurveIdFromTrace(trace);

    if (!curveId) {
      return trace;
    }

    if (sessionVisibility[curveId] === false) {
      return {
        ...trace,
        visible: CURVE_SESSION_LEGEND_MASK_VISIBLE,
      };
    }

    return {
      ...trace,
      visible: true,
    };
  });
}

/**
 * Finds the index of the first Plotly-visible trace.
 *
 * @param traces - Plotly traces, optionally annotated with session visibility.
 * @returns Index of the hover leader trace, or `-1` when none are visible.
 */
export function resolveTimeseriesHoverLeaderIndex<
  T extends CurveSessionVisibilityTraceLike,
>(traces: readonly T[]): number {
  return traces.findIndex((trace) => isTracePlotlyVisible(trace));
}
